/*
 * Иконки интерфейса.
 *
 * Контуры взяты из Lucide 0.469.0 (лицензия ISC — атрибуция в игре
 * не требуется, достаточно уведомления в исходниках). Вставлены
 * напрямую, без пакета: нужно десять иконок, а не тысяча.
 * https://lucide.dev — Copyright (c) Lucide Contributors 2022.
 */

type IconName =
  | 'check'
  | 'lock'
  | 'imagePlus'
  | 'ticket'
  | 'zap'
  | 'swords'
  | 'compass'
  | 'sparkles'
  | 'users'
  | 'user'
  | 'info'
  | 'history'
  | 'award'
  | 'x';

const PATHS: Readonly<Record<IconName, readonly string[]>> = {
  check: ['M20 6 9 17l-5-5'],
  lock: [
    'M5 11h14a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2Z',
    'M7 11V7a5 5 0 0 1 10 0v4',
  ],
  imagePlus: [
    'M16 5h6',
    'M19 2v6',
    'M21 11.5V19a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7.5',
    'm21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21',
    'M9 7a2 2 0 1 0 0 4 2 2 0 0 0 0-4Z',
  ],
  ticket: [
    'M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2Z',
    'M13 5v2',
    'M13 17v2',
    'M13 11v2',
  ],
  zap: [
    'M4 14a1 1 0 0 1-.78-1.63l9.9-10.2a.5.5 0 0 1 .86.46l-1.92 6.02A1 1 0 0 0 13 10h7a1 1 0 0 1 .78 1.63l-9.9 10.2a.5.5 0 0 1-.86-.46l1.92-6.02A1 1 0 0 0 11 14z',
  ],
  swords: [
    'M14.5 17.5 3 6V3h3l11.5 11.5',
    'M13 19l6-6',
    'M16 16l4 4',
    'M19 21l2-2',
    'M14.5 6.5 18 3h3v3l-3.5 3.5',
    'M5 14l4 4',
    'M7 17l-3 3',
    'M3 19l2 2',
  ],
  compass: [
    'm16.24 7.76-1.804 5.411a2 2 0 0 1-1.265 1.265L7.76 16.24l1.804-5.411a2 2 0 0 1 1.265-1.265z',
    'M22 12a10 10 0 1 1-20 0 10 10 0 0 1 20 0Z',
  ],
  sparkles: [
    'M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z',
    'M20 3v4',
    'M22 5h-4',
  ],
  users: [
    'M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2',
    'M9 3a4 4 0 1 0 0 8 4 4 0 0 0 0-8Z',
    'M22 21v-2a4 4 0 0 0-3-3.87',
    'M16 3.13a4 4 0 0 1 0 7.75',
  ],
  user: ['M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2', 'M12 3a4 4 0 1 0 0 8 4 4 0 0 0 0-8Z'],
  info: ['M22 12a10 10 0 1 1-20 0 10 10 0 0 1 20 0Z', 'M12 16v-4', 'M12 8h.01'],
  history: ['M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8', 'M3 3v5h5', 'M12 7v5l4 2'],
  award: [
    'm15.477 12.89 1.515 8.526a.5.5 0 0 1-.81.47l-3.58-2.687a1 1 0 0 0-1.197 0l-3.586 2.686a.5.5 0 0 1-.81-.469l1.514-8.526',
    'M18 8a6 6 0 1 1-12 0 6 6 0 0 1 12 0Z',
  ],
  x: ['M18 6 6 18', 'm6 6 12 12'],
};

export function Icon({
  name,
  size = 20,
  strokeWidth = 2,
}: {
  readonly name: IconName;
  readonly size?: number;
  readonly strokeWidth?: number;
}): React.ReactElement {
  return (
    <svg
      className="icon"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {PATHS[name].map((d) => (
        <path key={d} d={d} />
      ))}
    </svg>
  );
}

export type { IconName };
